import { ref, inject } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'

export interface CarouselImage {
  src: string
  alt: string
}

export interface CarouselState {
  isLoading: Ref<boolean>
  hasError: Ref<boolean>
  title: Ref<string>
  description: Ref<string>
  images: Ref<CarouselImage[]>
  fetchCarouselData: () => Promise<void>
  $t: (key: string, fallback?: string) => string
}

export default function useCarousel(passedContent?: any): CarouselState {
  const ContentService: any = inject('ContentService')
  const { t, locale } = useI18n()

  const isLoading = ref(true)
  const hasError = ref(false)
  const title = ref('')
  const description = ref('')
  const images = ref<CarouselImage[]>([])

  const getMediaUrl = (input: any): string => {
    if (!input) return ''
    if (ContentService?.getMediaUrl) return ContentService.getMediaUrl(input)
    if (typeof input === 'string') return input
    if (input?.url) return input.url
    if (input?.mediaUrl) return input.mediaUrl
    return ''
  }

  const applyContent = (content: any) => {
    if (!content) return

    title.value = content?.title || content?.carouselTitle || ''
    description.value = content?.description || content?.carouselDescription || ''

    const raw = content?.carouselImages || content?.images || []
    const list: CarouselImage[] = []

    if (Array.isArray(raw)) {
      for (const item of raw) {
        const srcCandidate = item?.imageUrl || item?.url || item?.src || item?.image || item
        const src = getMediaUrl(srcCandidate)
        const alt = item?.altText || item?.alt || item?.title || ''
        if (src) list.push({ src, alt })
      }
    }

    images.value = list
  }

  const getPageCulture = (): string | null => {
    if (typeof window === 'undefined') return null
    const pageCulture = (window as any)?.umbracoPageData?.culture
    return typeof pageCulture === 'string' && pageCulture ? pageCulture : null
  }

  const fetchCarouselData = async () => {
    try {
      // Show passed content immediately (no spinner), then fetch fresh data
      isLoading.value = !passedContent
      hasError.value = false

      if (passedContent) {
        applyContent(passedContent)
      }

      const pageCulture = getPageCulture()
      const culture = pageCulture || (locale.value === 'nl' ? 'nl-nl' : 'en-us')
      const homepageData = await ContentService?.getHomepage?.(culture)
      
      console.log('[Carousel] Homepage data received:', homepageData)
      console.log('[Carousel] Carousel images:', homepageData?.carouselImages)
      
      if (homepageData) {
        // API returns carouselImages at root
        applyContent({
          title: homepageData?.carouselTitle || '',
          description: homepageData?.carouselDescription || '',
          carouselImages: homepageData?.carouselImages || []
        })
      }
      
      console.log('[Carousel] Final images loaded:', images.value.length)
    } catch (error) {
      console.error('[Carousel] Error fetching carousel data:', error)
      hasError.value = true
      title.value = ''
      description.value = ''
      images.value = []
    } finally {
      isLoading.value = false
    }
  }

  // Kick off fetch automatically for components using this composable
  // Note: Called by the component's lifecycle, not here, to keep symmetry with Hero

  return {
    isLoading,
    hasError,
    title,
    description,
    images,
    fetchCarouselData,
    $t: t
  }
}
